import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { m } from "framer-motion";
import { useInView } from "react-intersection-observer";

export const FAQ = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const faqs = [
    {
      question: "How does youXforma work?",
      answer:
        "youXforma connects to your Figma files and uses advanced AI to analyze your designs. It automatically extracts colors, typography, spacing, components, and layout information, then generates comprehensive developer-ready documentation in seconds.",
    },
    {
      question: "Is my design data secure and private?",
      answer:
        "Absolutely. We use bank-level encryption (AES-256) for all data transfers and storage. Your designs are processed securely on our servers and never shared with third parties. We're SOC 2 Type II compliant and take security very seriously.",
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer:
        "Yes! You can cancel your subscription at any time with no penalties or fees. Your access will continue until the end of your current billing period, and you won't be charged again. We make it easy to cancel right from your account settings.",
    },
    {
      question: "What export formats are supported?",
      answer:
        "youXforma supports PDF, JSON, Markdown, HTML, and CSS exports. Pro plans also include direct integrations with Notion, Confluence, and Slack for seamless sharing with your team.",
    },
    {
      question: "Do you offer refunds?",
      answer:
        "Yes, we offer a 14-day money-back guarantee. If you're not satisfied with youXforma for any reason within the first 14 days, contact our support team and we'll issue a full refund, no questions asked.",
    },
    {
      question: "How accurate is the AI extraction?",
      answer:
        "Our AI is trained on millions of design files and achieves 95%+ accuracy in extracting design specs. It continuously learns and improves. For the rare cases where manual adjustments are needed, you can easily edit any extracted information.",
    },
    {
      question: "Can I use it with Sketch or Adobe XD?",
      answer:
        "Currently, youXforma is optimized for Figma, which represents 90%+ of the design tool market. We're exploring support for other tools based on user demand. Join our community to vote on which integrations we should build next!",
    },
    {
      question: "Do you offer team discounts?",
      answer:
        "Yes! Teams of 5+ users get volume discounts. Contact our sales team for a custom quote. We also offer educational discounts for students and non-profit organizations.",
    },
  ];

  // Generate FAQ schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <section className="section-padding bg-gradient-to-b from-muted/20 to-background" id="faq" ref={ref}>
      {/* FAQ Schema Markup for Rich Snippets */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      
      <div className="container-custom max-w-4xl">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-xl text-muted-foreground">
            Everything you need to know about youXforma
          </p>
        </m.div>
        
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <m.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ delay: index * 0.08, duration: 0.8 }}
            >
              <AccordionItem
                value={`item-${index}`}
                className="glass-card px-6 border-0"
              >
                <AccordionTrigger className="text-left text-lg font-semibold hover:text-primary transition-colors duration-500">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            </m.div>
          ))}
        </Accordion>

        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-center mt-12"
        >
          <p className="text-muted-foreground mb-4">Still have questions?</p>
          <Button variant="outline" size="lg">
            Contact Support
          </Button>
        </m.div>
      </div>
    </section>
  );
};
